import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import React, { useMemo, useState, useEffect } from 'react';

import {
  Box,
  Button,
  Tooltip,
  TextField,
  IconButton,
  Typography,
  Autocomplete,
} from '@mui/material';

import { useToast } from 'src/hooks/useToast';
import useResponsiveScreen from 'src/hooks/useResponsiveScreen';

import { genericApiToaster } from 'src/services/utils';
import { fetchTagsByDepartment } from 'src/services/tag.service';

import Iconify from '../iconify';
import { boxStyle } from './style';
import TextInput from '../FormInputs/TextInput';

function TagsTicket({
  row,
  selectedDepartment,
  setValue,
  tags,
  errorsTags,
  setErrorsTags,
  hasWritePermission,
}) {
  const { isSmallScreen } = useResponsiveScreen();
  const { t } = useTranslation();
  const { showToast, hideToast } = useToast();
  const [availableTags, setAvailableTags] = useState([]);

  const handleFetchTagsByDepartment = async () => {
    genericApiToaster(
      fetchTagsByDepartment,
      [selectedDepartment],
      '',
      t('errorMessages.failedToFetchTags'),
      showToast,
      hideToast,
      t,
      setAvailableTags,
      undefined
    );
  };

  useEffect(() => {
    if (selectedDepartment) handleFetchTagsByDepartment();
    setValue('tags', [...tags], { shouldDirty: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row, selectedDepartment]);

  const handleAddTag = () => {
    const newTag = { id: Date.now(), name: '', value: '' };
    setErrorsTags((prevErrors) => ({
      ...prevErrors,
      [newTag.id]: {
        name: t('formControl.fieldRequired'),
        value: t('formControl.fieldRequired'),
      },
    }));
    setValue('tags', [...tags, newTag], { shouldDirty: true });
  };

  const handleRemoveTag = (id) => {
    const updatedTags = tags.filter((tag) => tag.id !== id);
    setErrorsTags((prevErrors) => {
      const newErrors = { ...prevErrors };
      delete newErrors[id];
      return newErrors;
    });
    setValue('tags', updatedTags, { shouldDirty: true });
  };

  const handleTagSelection = (id, key, value) => {
    const updatedTags = tags.map((tag) =>
      tag.id === id ? { ...tag, [key]: value } : tag
    );
    setValue('tags', updatedTags, { shouldDirty: true });
  };

  const handleTagChange = (tagId, key, newValue) => {
    setErrorsTags((prevErrors) => {
      const newErrors = { ...prevErrors };

      // Ensure the tag's error object exists
      if (!newErrors[tagId]) {
        newErrors[tagId] = {};
      }

      if (key === 'name') {
        const isDuplicate = tags.some(
          (tag) =>
            (tag?.tag?.name === newValue || tag?.name === newValue) && tag.id !== tagId
        );

        if (isDuplicate) {
          newErrors[tagId].name = t('formControl.duplicateTag');
        } else if (!newValue?.trim()) {
          newErrors[tagId].name = t('formControl.fieldRequired');
        } else {
          delete newErrors[tagId].name;
        }
      }

      if (key === 'value') {
        if (!newValue?.trim()) {
          newErrors[tagId].value = t('formControl.fieldRequired');
        } else {
          delete newErrors[tagId].value;
        }
      }

      // If both name and value errors are cleared, remove the tag's error object
      if (Object.keys(newErrors[tagId]).length === 0) {
        delete newErrors[tagId];
      }

      return newErrors;
    });

    handleTagSelection(tagId, key, newValue);
  };

  const filteredAvailableTags = useMemo(
    () =>
      availableTags.filter(
        (tag) => !tags.some((t) => t?.tag?.name === tag.name || t.name === tag.name)
      ),
    [availableTags, tags]
  );

  return (
    <>
      <Typography
        variant="subtitle2"
        sx={{ fontSize: isSmallScreen ? '0.675rem' : '1rem', mt: '2vh' }}
      >
        {t('attributes.tags')}
      </Typography>
      {tags.map((tag, index) => (
        <Box key={tag.id} sx={boxStyle}>
          {/* Tag Autocomplete Input */}
          <Autocomplete
            freeSolo
            options={filteredAvailableTags.map((availableTag) => availableTag.name)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('attributes.tag')}
                variant="outlined"
                error={!!errorsTags[tag.id]?.name}
                helperText={errorsTags[tag.id]?.name}
              />
            )}
            disabled={!!tag?.tag?.name}
            value={tag?.tag?.name}
            onChange={(event, newValue) => handleTagChange(tag.id, 'name', newValue)}
            onInputChange={(event, newValue) => handleTagChange(tag.id, 'name', newValue)}
            sx={{ width: '45%' }}
            size="small"
          />

          {/* Tag Value Input */}
          <TextInput
            defaultValue={tag?.value}
            disabled={!hasWritePermission}
            fullWidth
            label={t('attributes.value')}
            size="small"
            sx={{ width: '45%' }}
            onChange={(e) => handleTagChange(tag.id, 'value', e.target.value)}
            error={!!errorsTags[tag.id]?.value}
            helperText={errorsTags[tag.id]?.value}
          />

          {/* Delete Icon */}
          {hasWritePermission && (
            <Tooltip title={t('buttons.delete')}>
              <IconButton onClick={() => handleRemoveTag(tag.id)}>
                <Iconify icon="eva:trash-2-fill" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ))}

      {/* Add Tag Button */}
      {hasWritePermission && (
        <Box sx={{ mt: 1.5, pl: 2 }}>
          <Button variant="contained" onClick={handleAddTag} size="small">
            <Iconify icon="typcn:plus" sx={{ mr: 1 }} />
            {t('buttons.addTag')}
          </Button>
        </Box>
      )}
    </>
  );
}

TagsTicket.propTypes = {
  row: PropTypes.object,
  selectedDepartment: PropTypes.number,
  setValue: PropTypes.func,
  tags: PropTypes.array,
  errorsTags: PropTypes.object,
  setErrorsTags: PropTypes.func,
  hasWritePermission: PropTypes.bool,
};

export default TagsTicket;
